import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import Tooltip from '@groovehq/internal-design-system/lib/components/Tooltip/Tooltip'
import { Menu } from 'util/ui'
import {
  OPENSEARCH_ONLY_MAX_LOOKBACK_DAYS,
  START_OF_REPORTS,
} from 'ducks/reports/constants'
import {
  selectReportsMinStartDate,
  selectTimezoneSource,
  selectTimezoneWithOffsetDisplay,
} from 'ducks/reports/controls/selectors'
import { selectMaxReportingDays } from 'ducks/billing/selectors/features'
import { isDefined } from 'util/nullOrUndefinedChecks'
import BusinessHoursControl from '../BusinessHoursControl'
import DateControl from '../DateControl'
import styles from './styles.less'

// The billing limit wins when it is tighter than the OpenSearch-only lookback.
function isBillingLimitTighter(maxReportingDays) {
  if (!isDefined(maxReportingDays)) return false
  const days = Number(maxReportingDays)
  return !isNaN(days) && days - 1 < OPENSEARCH_ONLY_MAX_LOOKBACK_DAYS
}

export default function ControlBar({ prerenderDatePicker }) {
  const source = useSelector(selectTimezoneSource)
  const timezone = useSelector(selectTimezoneWithOffsetDisplay)
  const maxReportingDays = useSelector(selectMaxReportingDays)
  const minStartDate = useSelector(selectReportsMinStartDate)
  const limitToMinStart =
    !!minStartDate && !isBillingLimitTighter(maxReportingDays)

  const tooltipTitle = useMemo(
    () => {
      switch (source) {
        case 'ACCOUNT':
          return 'Account timezone used'
        case 'BUSINESS_HOURS':
          return 'Business hours timezone used'
        case 'BROWSER':
          return 'Browser timezone used'
        default:
          return 'Unknown timezone used'
      }
    },
    [source]
  )

  return (
    <Menu fixed="top" className={styles.ControlBar}>
      <Menu.Menu position="left">
        <Menu.Item fitted>
          <BusinessHoursControl />
        </Menu.Item>
      </Menu.Menu>
      <Menu.Menu position="left">
        <Menu.Item>
          <Tooltip title={tooltipTitle}>
            <i>Timezone Used: {timezone}</i>
          </Tooltip>
        </Menu.Item>
        {limitToMinStart && (
          <Menu.Item>
            <i>
              Reports are limited to the last{' '}
              {OPENSEARCH_ONLY_MAX_LOOKBACK_DAYS} days
            </i>
          </Menu.Item>
        )}
      </Menu.Menu>
      <Menu.Menu position="right">
        <Menu.Item fitted>
          <DateControl
            prerender={prerenderDatePicker}
            // Stored/URL ranges older than the OpenSearch-only limit are reset
            // even when a tighter billing minDays drives the calendar.
            earliestStartDate={minStartDate || START_OF_REPORTS}
            coerceToEarliestStartDate={!!minStartDate}
            presetMinStartDate={minStartDate}
            // minDays overrides earliestStartDate if defined
            minDays={limitToMinStart ? undefined : maxReportingDays}
          />
        </Menu.Item>
      </Menu.Menu>
    </Menu>
  )
}
